import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  faviconGuessUrl,
  fetchMetadata,
  metadataFallback,
  safeFaviconUrl,
  type LinkMetadata,
} from "./fetchMetadata";
import { useTheme } from "../context/ThemeProvider";
import {
  displayReadableUrl,
  hostnameFromUrl,
  titleFromUrl,
} from "../utils/displayReadableUrl";

/** Classic OG / Twitter card ratio — large card slot height known from frame 0 */
const OG_ASPECT = "1200 / 630";

export type WebpageFields = {
  link_url?: string | null;
  link_title?: string | null;
  link_description?: string | null;
  link_image?: string | null;
  link_favicon?: string | null;
  link_site_name?: string | null;
};

function LinkIcon({ dark }: { dark: boolean }) {
  return (
    <div
      className={`flex h-12 w-12 min-w-12 shrink-0 items-center justify-center rounded-lg ${dark ? "bg-[#3a3a3a]" : "bg-[#e8e8e8]"}`}
      aria-hidden
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke={dark ? "#9aa0a6" : "#5f6368"}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    </div>
  );
}

/**
 * Telegram-style chat link: normal selectable text, underline on hover,
 * click opens (skipped if user was dragging to select).
 */
export function SelectableChatLink({
  href,
  className = "",
  children,
}: {
  href: string;
  className?: string;
  children: ReactNode;
}) {
  const downRef = useRef<{ x: number; y: number } | null>(null);

  const open = () => {
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <span
      role="link"
      tabIndex={0}
      className={`cursor-text select-text break-all underline-offset-[3px] transition-[text-decoration-color] hover:cursor-pointer hover:underline ${className}`.trim()}
      onContextMenu={(e) => e.preventDefault()}
      onMouseDown={(e) => {
        if (e.button === 0) downRef.current = { x: e.clientX, y: e.clientY };
      }}
      onClick={(e) => {
        e.stopPropagation();
        const start = downRef.current;
        downRef.current = null;
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && (sel.toString() || "").length > 0) {
          return;
        }
        if (
          start &&
          Math.hypot(e.clientX - start.x, e.clientY - start.y) > 5
        ) {
          return;
        }
        open();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
    >
      {children}
    </span>
  );
}

function FaviconSlot({
  src,
  dark,
}: {
  src: string;
  dark: boolean;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return <LinkIcon dark={dark} />;
  }

  return (
    <div
      className={`flex h-12 w-12 min-w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg ${dark ? "bg-[#3a3a3a]" : "bg-[#e8e8e8]"}`}
    >
      <img
        src={src}
        alt=""
        width={28}
        height={28}
        decoding="async"
        className="h-7 w-7 object-contain"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function fromWebpage(href: string, webpage?: WebpageFields | null): LinkMetadata | null {
  if (!webpage) return null;
  const title = String(webpage.link_title || "").trim();
  const image = String(webpage.link_image || "").trim();
  const favicon = String(webpage.link_favicon || "").trim();
  const description = String(webpage.link_description || "").trim();
  const publisher = String(webpage.link_site_name || "").trim();
  // Server sent something useful — trust wire (TG Webpage)
  if (!title && !image && !favicon && !publisher) return null;
  return {
    title: title || titleFromUrl(href),
    description,
    image,
    favicon: favicon || faviconGuessUrl(href),
    publisher: publisher || hostnameFromUrl(href),
  };
}

function hasServerWebpage(webpage?: WebpageFields | null) {
  if (!webpage) return false;
  return !!(
    String(webpage.link_title || "").trim() ||
    String(webpage.link_image || "").trim() ||
    String(webpage.link_favicon || "").trim() ||
    String(webpage.link_site_name || "").trim()
  );
}

type Props = {
  url: string;
  right: boolean;
  /** Persist fields from history / ack / realtime — preferred over client crawl */
  webpage?: WebpageFields | null;
};

/**
 * Telegram Webpage bubble:
 * - server `link_image` → large card from first frame (placeholder → bitmap)
 * - no `link_image` → compact forever (no compact→large grow on scroll)
 * - thin history without link_* → idle enrich via /config/link-preview (fallback)
 */
const LinkPreview = ({ url, right, webpage }: Props) => {
  const href = String(webpage?.link_url || url || "").trim();
  const fallbackTitle = titleFromUrl(href);
  const fallbackHost = hostnameFromUrl(href);
  const dark = useTheme().theme === "dark";
  const serverMeta = fromWebpage(href, webpage);

  const [meta, setMeta] = useState<LinkMetadata>(
    () => serverMeta || metadataFallback(href),
  );
  /** Large layout reserved when server (or enrich) says there is an OG image */
  const [wantsLarge, setWantsLarge] = useState(
    () => !!(serverMeta?.image || "").trim(),
  );
  const [ogSrc, setOgSrc] = useState(() => (serverMeta?.image || "").trim());
  const [ogLoaded, setOgLoaded] = useState(false);
  const [ogFailed, setOgFailed] = useState(false);
  const decodeGen = useRef(0);

  useEffect(() => {
    const nextServer = fromWebpage(href, webpage);
    const known = hasServerWebpage(webpage);
    decodeGen.current += 1;
    const gen = decodeGen.current;

    if (nextServer) {
      setMeta(nextServer);
      const img = (nextServer.image || "").trim();
      setWantsLarge(!!img);
      setOgSrc(img);
      setOgLoaded(false);
      setOgFailed(false);
      // No client crawl when server already shaped the Webpage
      return;
    }

    // Thin history: instant compact, then idle enrich (same /config/link-preview)
    const initial = metadataFallback(href);
    setMeta(initial);
    setWantsLarge(false);
    setOgSrc("");
    setOgLoaded(false);
    setOgFailed(false);

    let cancelled = false;
    const idle =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback
        : (cb: () => void) => window.setTimeout(cb, 400);

    const cancelIdle =
      typeof cancelIdleCallback === "function"
        ? cancelIdleCallback
        : (id: number) => clearTimeout(id);

    const idleId = idle(() => {
      if (cancelled || gen !== decodeGen.current || known) return;
      fetchMetadata(href).then((data) => {
        if (cancelled || gen !== decodeGen.current) return;
        const nextFavicon = safeFaviconUrl(href, data.favicon);
        setMeta({
          title: data.title || initial.title,
          description: data.description || "",
          publisher: data.publisher || initial.publisher,
          favicon: nextFavicon,
          image: data.image || "",
        });
        if (data.image) {
          setWantsLarge(true);
          setOgSrc(data.image);
          setOgLoaded(false);
          setOgFailed(false);
        }
      });
    });

    return () => {
      cancelled = true;
      cancelIdle(idleId as number);
    };
  }, [
    href,
    webpage?.link_url,
    webpage?.link_title,
    webpage?.link_description,
    webpage?.link_image,
    webpage?.link_favicon,
    webpage?.link_site_name,
  ]);

  const title = meta.title || fallbackTitle || href;
  const siteLabel = meta.publisher || fallbackHost;
  const readable = displayReadableUrl(href);
  const faviconSrc = safeFaviconUrl(href, meta.favicon);
  const showLarge = wantsLarge && !ogFailed;

  const linkAccent = right
    ? dark
      ? "text-sky-200"
      : "text-sky-100"
    : "text-[#027EB5]";

  return (
    <div
      className={`w-[18rem] max-w-full rounded-xl text-sm sm:w-[20.5rem] ${right ? "" : "bg-otherMessageBg"} ${dark ? "text-white" : "text-black"}`}
    >
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={href}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
        className="block w-full"
      >
        <div
          className={`w-full overflow-hidden rounded-xl ${dark ? "bg-[#2a2a2a]" : "bg-secondary"}`}
        >
          {/* Height reserved from frame 0 when large — no compact→large jump */}
          {showLarge ? (
            <div
              className={`relative w-full overflow-hidden ${dark ? "bg-[#222]" : "bg-[#ddd]"}`}
              style={{ aspectRatio: OG_ASPECT }}
            >
              {ogSrc ? (
                <img
                  src={ogSrc}
                  alt=""
                  width={1200}
                  height={630}
                  decoding="async"
                  draggable={false}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${
                    ogLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  onLoad={() => setOgLoaded(true)}
                  onError={() => {
                    // Keep large slot + placeholder; never swap to favicon
                    setOgFailed(false);
                    setOgSrc("");
                    setOgLoaded(false);
                    setMeta((m) => ({ ...m, image: "" }));
                  }}
                />
              ) : null}
            </div>
          ) : null}

          <div className="flex w-full items-start gap-2.5 p-2">
            {!showLarge ? (
              <div className="shrink-0">
                <FaviconSlot src={faviconSrc} dark={dark} />
              </div>
            ) : null}

            <div className="min-w-0 flex-1 self-center overflow-hidden">
              <p className="line-clamp-2 text-sm font-normal leading-normal">
                {title}
              </p>
              {showLarge && meta.description ? (
                <p className="mt-0.5 line-clamp-2 text-xs leading-snug opacity-60">
                  {meta.description}
                </p>
              ) : null}
              {siteLabel ? (
                <p
                  className={`mt-1 truncate text-xs font-medium ${dark ? "text-[#5eb8e8]" : "text-[#027EB5]"}`}
                >
                  {siteLabel}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </a>

      <div className="px-2 pb-1 pt-1 text-sm leading-snug">
        <SelectableChatLink href={href} className={linkAccent}>
          {readable}
        </SelectableChatLink>
      </div>
    </div>
  );
};

export default LinkPreview;
